import { compose, concat, isNil, pathOr, propOr, T } from 'ramda';
import { createAction } from 'redux-actions';

import {
	deleteData,
	getData,
	patchApplicationJson,
	postApplicationJson,
	postData,
	putApplicationJson,
	withOAuthRefreshToken,
} from '@gef-ui/middleware-api/auth/oauth2/facade';
import {
	getBinaryData,
	getData as getDataWithoutTokenWrapperGef,
	postApplicationJsonBinaryData as postBinaryData,
} from '@gef-ui/middleware-api/facade';

import customErrorReport from 'features/views/errorReport';

import * as types from './actionTypes';
import { empty } from './model';
import { generateUrl } from './utils';

const latestRequests = {};

const getFailure = propOr(empty, 'failure');

const getMetaData = pathOr(empty, ['body', 'pageMetaData']);

export const onRequest = (path, replaceParams, queryParams, body) =>
	createAction(types.FETCH_REQUEST, () => ({
		path,
		replaceParams,
		queryParams,
		body,
	}));

/* @SuppressImportWarn */
export const onSuccess = (path) =>
	createAction(types.FETCH_ON_SUCCESS, (response) => ({
		path,
		metaData: getMetaData(response),
	}));

/* @SuppressImportWarn */
export const onError = (path) =>
	createAction(types.FETCH_ON_ERROR, (response) => ({
		path,
		data: getFailure(response),
	}));

export const updateFetchRequestMetadata = (path, uniqueReqID) =>
	createAction(types.FETCH_UPDATE_METADATA, (metaData) => ({
		path: `${path}${!isNil(uniqueReqID) ? `.${uniqueReqID}` : ''}`,
		metaData,
	}));

// workaround to work with old shouldReportError prop - which has logic with response / failure
const reportErrorToGefGlobalHandler = createAction('REPORT_ERROR', (error) => error);

const getTraceId = (traceparent) => traceparent?.split('-')[1];

// for connection with GEF's ErrorAppbarButton, which is used in Navbar
const transformError = (error) => ({
	...error,
	meta: {
		...error?.meta,
		errorReport: {
			...error?.meta?.errorReport,
			heading: error.parsedContent?.message,
			shortDescription: error.parsedContent?.message || error.message,
			longDescription: error.parsedContent?.type,
			screen: window.screenId,
			traceId: getTraceId(error.traceparent),
		},
	},
});

const createFetchConfig = (url, options = {}, errorReport = {}) => {
	const {
		uniqueReqID,
		replaceParams = {},
		queryParams = {},
		additionalHeaders = {},
		customOkResponseResolver,
		body,
		takeLatest = false,
		shouldReportError = T,
	} = options;

	const fetchStoreKey = `${url}${!isNil(uniqueReqID) ? `.${uniqueReqID}` : ''}`;
	const requestId = new Date().valueOf();

	if (takeLatest) {
		latestRequests[fetchStoreKey] = requestId;
	}

	return {
		endpoint: generateUrl(url, replaceParams, queryParams),
		body,
		additionalHeaders,
		runInBatch: true,
		customOkResponseResolver,
		meta: {
			requestUuid: fetchStoreKey,
			errorReport,
		},
		cache: {
			cacheKey: fetchStoreKey,
		},
		actions: {
			onRequest: concat(options.onRequest || [], [onRequest(fetchStoreKey, replaceParams, queryParams, body)]),
			onSuccess: (response) => (dispatch) => {
				if (takeLatest && requestId !== latestRequests[fetchStoreKey]) return;
				const actionCreators = concat(options.onSuccess || [], [onSuccess(fetchStoreKey)]);
				actionCreators.map((actionCreator) => dispatch(actionCreator(response)));
			},
			onError: (response) => (dispatch) => {
				if (takeLatest && requestId !== latestRequests[fetchStoreKey]) return;
				if (shouldReportError(response)) {
					dispatch(customErrorReport.actions.reportError(response.failure, url));
					dispatch(reportErrorToGefGlobalHandler(response?.failure));
				}
				const actionCreators = concat(options.onError || [], [onError(fetchStoreKey)]);
				actionCreators.map((actionCreator) => dispatch(actionCreator(response)));
			},
		},
		errorManipulation: transformError,
		notReportError: true,
	};
};

export const clearFetchStore = createAction(types.FETCH_CLEAR, (path) => {
	latestRequests[path] = new Date().valueOf();
	return { path };
});

export const get = compose(getData, createFetchConfig);

export const getBinary = compose(withOAuthRefreshToken(getBinaryData), createFetchConfig);

export const getDataWithoutTokenWrapper = compose(getDataWithoutTokenWrapperGef, createFetchConfig);

export const post = compose(postApplicationJson, createFetchConfig);

export const postBinary = compose(withOAuthRefreshToken(postBinaryData), createFetchConfig);

export const patch = compose(patchApplicationJson, createFetchConfig);

export const put = compose(putApplicationJson, createFetchConfig);

export const deleteRequest = compose(deleteData, createFetchConfig);

export const uploadData = compose(postData, createFetchConfig);

export default {
	get,
	getBinary,
	getDataWithoutTokenWrapper,
	post,
	postBinary,
	uploadData,
	put,
	patch,
	deleteRequest,
	clearFetchStore,
};
